// Per-plane fading vapor trail.
// Each tick we sample plane.position into a ring buffer and rebuild a thin
// ribbon mesh whose alpha fades along the trail. Cheap because we keep at
// most ~32 segments per plane; total vertex count is bounded.

import * as THREE from 'three';
import type { Plane } from '../plane/Plane';

const SEGMENTS = 28;
const SAMPLE_INTERVAL = 0.05; // seconds between samples

const trailMatBlue = new THREE.MeshBasicMaterial({
  color: 0xc8e6ff, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
});
const trailMatRed = new THREE.MeshBasicMaterial({
  color: 0xffd0c8, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
});

interface PlaneTrail {
  plane: Plane;
  positions: THREE.Vector3[];
  ages: number[];
  geom: THREE.BufferGeometry;
  mesh: THREE.Mesh;
  sampleAcc: number;
}

export class TrailFx {
  scene: THREE.Scene;
  trails: PlaneTrail[] = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  attach(plane: Plane): void {
    const geom = new THREE.BufferGeometry();
    // Pre-allocate buffer for SEGMENTS * 2 vertices (ribbon strips of 2 verts each).
    const verts = new Float32Array(SEGMENTS * 2 * 3);
    const colors = new Float32Array(SEGMENTS * 2 * 4);
    geom.setAttribute('position', new THREE.BufferAttribute(verts, 3));
    geom.setAttribute('color', new THREE.BufferAttribute(colors, 4));
    geom.setDrawRange(0, 0);
    const indices: number[] = [];
    for (let i = 0; i < SEGMENTS - 1; i++) {
      const a = i * 2, b = i * 2 + 1, c = (i + 1) * 2, d = (i + 1) * 2 + 1;
      indices.push(a, b, c);
      indices.push(b, d, c);
    }
    geom.setIndex(indices);

    const mesh = new THREE.Mesh(geom, plane.team === 'blue' ? trailMatBlue : trailMatRed);
    mesh.frustumCulled = false;
    this.scene.add(mesh);

    this.trails.push({
      plane,
      positions: [],
      ages: [],
      geom,
      mesh,
      sampleAcc: 0,
    });
  }

  update(dt: number): void {
    for (const t of this.trails) {
      t.sampleAcc += dt;
      // Push a new sample every SAMPLE_INTERVAL.
      if (t.plane.alive && t.sampleAcc >= SAMPLE_INTERVAL) {
        t.sampleAcc = 0;
        const tail = t.plane.position.clone().addScaledVector(t.plane.forward, -1.0);
        t.positions.push(tail);
        t.ages.push(0);
        if (t.positions.length > SEGMENTS) {
          t.positions.shift();
          t.ages.shift();
        }
      }
      // Age existing samples.
      for (let i = 0; i < t.ages.length; i++) t.ages[i] += dt;

      // Build ribbon: each sample i emits two verts offset along plane.right.
      const positions = t.geom.getAttribute('position') as THREE.BufferAttribute;
      const right = t.plane.right;
      let segCount = 0;
      const offset = 0.4;
      for (let i = 0; i < t.positions.length; i++) {
        const p = t.positions[i];
        const ageT = Math.min(1, t.ages[i] / 1.4);
        const half = offset * (1 - ageT * 0.6);
        positions.setXYZ(i * 2,     p.x - right.x * half, p.y - right.y * half, p.z - right.z * half);
        positions.setXYZ(i * 2 + 1, p.x + right.x * half, p.y + right.y * half, p.z + right.z * half);
        segCount = i + 1;
      }
      positions.needsUpdate = true;
      t.geom.setDrawRange(0, Math.max(0, (segCount - 1) * 6));
    }
  }

  detach(plane: Plane): void {
    const idx = this.trails.findIndex((t) => t.plane === plane);
    if (idx === -1) return;
    const t = this.trails[idx];
    this.scene.remove(t.mesh);
    t.geom.dispose();
    this.trails.splice(idx, 1);
  }
}
